import type { ReactNode } from "react";
import { Permission, hasPermission, type Channel, type CommunityStructure } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { Button, IconButton } from "../../components/ui/Button";
import { toast } from "../../components/ui/Toast";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "../../components/ui/Menu";
import { IconDeafened, IconHangUp, IconHeadphones, IconMic, IconMicOff, IconScreenShare, IconScreenShareActive, IconStopStream, IconSwapSource, IconTuning } from "../../components/icons";
import { canShareScreen, useStreamUi, useVoice } from "./VoiceProvider";
import { qualityLabel } from "./stream";

/** Why you can't share your screen right now, or null when you can. The server checks independently. */
export function useShareBlockedReason(channel?: Channel | null, structure?: CommunityStructure | null): string | null {
  const voice = useVoice();
  if (voice.status !== "connected") return "Join to share your screen";
  if (!canShareScreen()) return "Screen sharing isn't available in this browser";
  if (voice.room?.kind === "stage" && !voice.self?.speaker) return "Only speakers can share their screen";
  if (voice.room?.kind !== "call" && channel && structure && !structure.community.isOwner && !hasPermission(channel.permissions, Permission.STREAM))
    return "You don't have permission to share your screen here";
  return null;
}

/** Go live (through the quality dialog), or, while live, a menu to change source or quality and stop. */
export function ShareButton({ reason, size = "md", className }: { reason: string | null; size?: "sm" | "md" | "lg"; className?: string }) {
  const voice = useVoice();
  const openGoLive = useStreamUi((s) => s.openGoLive);
  const live = voice.shareSettings;
  if (voice.localScreen && live) {
    const viewers = voice.self?.stream?.viewers.length ?? 0;
    return (
      <Menu>
        <MenuTrigger asChild>
          <IconButton size={size} label="Stream options" icon={IconScreenShareActive} active className={cn("text-success", className)} />
        </MenuTrigger>
        <MenuContent side="top" align="center">
          <MenuLabel>
            <span className="tabular">
              Live · {qualityLabel(live)}
              {viewers > 0 ? ` · ${viewers} watching` : ""}
            </span>
          </MenuLabel>
          <MenuItem icon={IconSwapSource} onSelect={() => void voice.changeShareSource()}>
            Change source
          </MenuItem>
          <MenuItem icon={IconTuning} onSelect={() => openGoLive("edit")}>
            Stream quality
          </MenuItem>
          <MenuSeparator />
          <MenuItem icon={IconStopStream} danger onSelect={voice.stopShare}>
            Stop streaming
          </MenuItem>
        </MenuContent>
      </Menu>
    );
  }
  const blocked = !!reason;
  return (
    <IconButton
      size={size}
      label={reason ?? "Share your screen"}
      icon={IconScreenShare}
      aria-disabled={blocked || undefined}
      aria-haspopup={blocked ? undefined : "dialog"}
      onClick={() => {
        if (reason) toast.show(reason);
        else openGoLive("start");
      }}
      className={cn(blocked && "opacity-40", className)}
    />
  );
}

/** Mute, deafen, share screen and leave, for whichever room you're in. */
export function VoiceControls({ shareReason, children, leaveLabel = "Leave" }: { shareReason: string | null; children?: ReactNode; leaveLabel?: string }) {
  const voice = useVoice();
  const audience = voice.room?.kind === "stage" && !voice.self?.speaker;
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {!audience && (
        <IconButton
          size="lg"
          label={voice.serverMuted ? "Muted by a moderator" : voice.muted ? "Unmute" : "Mute"}
          icon={voice.muted || voice.serverMuted ? IconMicOff : IconMic}
          onClick={voice.toggleMute}
          className={cn("bg-surface-2", (voice.muted || voice.serverMuted) && "text-danger")}
        />
      )}
      <IconButton size="lg" label={voice.deafened ? "Undeafen" : "Deafen"} icon={voice.deafened ? IconDeafened : IconHeadphones} onClick={voice.toggleDeafen} className={cn("bg-surface-2", voice.deafened && "text-danger")} />
      {!audience && <ShareButton size="lg" reason={shareReason} className="bg-surface-2" />}
      {children}
      <Button variant="danger" size="lg" icon={IconHangUp} onClick={voice.leave}>
        {leaveLabel}
      </Button>
    </div>
  );
}
